import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'

/**
 * Applies a theme preset to the current tenant in a single atomic transaction.
 * Uses the Supabase RPC `apply_theme_preset` which updates:
 *   - tenant_themes (colors, fonts, border-radius, custom_css)
 *   - tenants.homepage_sections
 *   - tenant_pages (about, events, members, music, shop) where is_system = true
 */
export function useApplyTheme() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (presetId: string) => {
      if (!tenantId) throw new Error('Tidak ada tenant yang dipilih')

      const { error } = await supabase.rpc('apply_theme_preset', {
        p_tenant_id: tenantId,
        p_preset_id: presetId,
      })

      if (error) throw error
    },
    onSuccess: async () => {
      // Invalidate all affected dashboard query caches
      qc.invalidateQueries({ queryKey: ['tenant-theme', tenantId] })
      qc.invalidateQueries({ queryKey: ['homepage-sections', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-pages', tenantId] })

      // Bust Next.js ISR cache on the public site
      await triggerRevalidation('tenant-theme')
      await triggerRevalidation('tenant-pages')
    },
  })
}
